import {
  CheckCircle,
  Lock,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  styled,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import logogpImage from '../assets/logogp.png';

const StyledContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#1E1E1E',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
    alignItems: 'flex-start',
    paddingTop: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200vw',
    height: '200vh',
    background: 'radial-gradient(circle at center, #2E2E2E 0%, #1E1E1E 50%)',
    opacity: 0.6,
    animation: 'pulse 8s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
    '50%': {
      transform: 'translate(-50%, -50%) scale(1.2)',
    },
    '100%': {
      transform: 'translate(-50%, -50%) scale(1)',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  background: 'rgba(18, 18, 18, 0.8)',
  backdropFilter: 'blur(10px)',
  borderRadius: '20px',
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  width: '100%',
  maxWidth: '500px',
  overflow: 'hidden',
  animation: 'fadeIn 0.5s ease-out',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: '16px',
    maxWidth: '100%',
  },
  '@keyframes fadeIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '2rem',
  [theme.breakpoints.down('sm')]: {
    marginBottom: '1.5rem',
  },
  '& img': {
    width: '120px',
    height: 'auto',
    filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)',
    animation: 'logoFloat 6s ease-in-out infinite',
    [theme.breakpoints.down('sm')]: {
      width: '100px',
    },
  },
  '@keyframes logoFloat': {
    '0%, 100%': {
      transform: 'translateY(0) rotate(0deg)',
    },
    '25%': {
      transform: 'translateY(-15px) rotate(5deg)',
    },
    '75%': {
      transform: 'translateY(15px) rotate(-5deg)',
    },
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    transition: 'all 0.3s ease',
    background: 'rgba(255, 255, 255, 0.05)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.08)',
    },
    '&.Mui-focused': {
      background: 'rgba(255, 255, 255, 0.1)',
      transform: 'scale(1.02)',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-input': {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.2)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#00FF00',
  },
}));

const StyledButton = styled(Button)({
  marginTop: '16px',
  padding: '12px',
  backgroundColor: '#00FF00',
  color: '#000000',
  fontWeight: 600,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#00CC00',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 255, 0, 0.3)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&.Mui-disabled': {
    backgroundColor: 'rgba(0, 255, 0, 0.5)',
  },
});

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
  },
}));

const TrocarSenha = () => {
  const navigate = useNavigate();
  const { accessToken, primeiroAcesso, updatePrimeiroAcesso } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [senhaRequirements, setSenhaRequirements] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  useEffect(() => {
    // Se não estiver autenticado ou não for primeiro acesso, redirecionar
    if (!accessToken) {
      navigate('/login');
      return;
    }

    if (!primeiroAcesso) {
      navigate('/');
      return;
    }
  }, [accessToken, primeiroAcesso, navigate]);

  // Validar requisitos da senha
  useEffect(() => {
    setSenhaRequirements({
      minLength: novaSenha.length >= 8,
      hasUpperCase: /[A-Z]/.test(novaSenha),
      hasLowerCase: /[a-z]/.test(novaSenha),
      hasNumber: /[0-9]/.test(novaSenha),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(novaSenha)
    });
  }, [novaSenha]);

  const validateSenha = () => {
    const requirements = senhaRequirements;
    return (
      requirements.minLength &&
      requirements.hasUpperCase &&
      requirements.hasLowerCase &&
      requirements.hasNumber &&
      requirements.hasSpecialChar
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validações
    if (!senhaAtual) {
      setError('Senha atual é obrigatória');
      return;
    }

    if (!novaSenha) {
      setError('Nova senha é obrigatória');
      return;
    }

    if (!validateSenha()) {
      setError('A nova senha não atende aos requisitos de segurança');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setError('As senhas não coincidem');
      return;
    }

    if (senhaAtual === novaSenha) {
      setError('A nova senha deve ser diferente da senha atual');
      return;
    }

    setLoading(true);

    try {
      await authService.trocarSenhaPrimeiroAcesso(accessToken, senhaAtual, novaSenha);
      
      // Atualizar estado de primeiro acesso
      updatePrimeiroAcesso(false);
      
      setSuccess(true);
      
      // Redirecionar após 2 segundos
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Erro ao trocar senha. Verifique os dados informados.');
    } finally {
      setLoading(false);
    }
  };

  const RequirementItem = ({ met, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {met ? (
        <CheckCircle sx={{ fontSize: 16, color: '#00FF00' }} />
      ) : (
        <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid', borderColor: 'rgba(255, 255, 255, 0.3)' }} />
      )}
      <Typography
        variant="body2"
        sx={{
          color: met ? '#00FF00' : 'rgba(255, 255, 255, 0.6)',
          textDecoration: met ? 'none' : 'none'
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (!accessToken || !primeiroAcesso) {
    return null;
  }

  return (
    <StyledContainer>
      <StyledPaper elevation={3}>
        <LogoContainer>
          <img 
            src={logogpImage} 
            alt="Logo GP" 
            style={{ 
              filter: 'brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)'
            }} 
          />
        </LogoContainer>

        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{
            mb: isMobile ? 2 : 3,
            fontWeight: 600,
            textAlign: 'center',
            color: '#00FF00',
            fontSize: isMobile ? '1.5rem' : undefined,
          }}
        >
          Primeiro Acesso
        </Typography>
        <Typography 
          variant="body2" 
          sx={{
            textAlign: 'center',
            mb: 4,
            color: 'rgba(255, 255, 255, 0.7)'
          }}
        >
          Por segurança, você precisa alterar sua senha antes de continuar
        </Typography>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(211, 47, 47, 0.1)',
              color: '#ff5252',
              border: '1px solid rgba(211, 47, 47, 0.3)'
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              color: '#00FF00',
              border: '1px solid rgba(0, 255, 0, 0.3)'
            }}
          >
            Senha alterada com sucesso! Redirecionando...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <StyledTextField
            fullWidth
            label="Senha Atual"
            type={showSenhaAtual ? 'text' : 'password'}
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            required
            disabled={loading || success}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                    edge="end"
                    disabled={loading || success}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showSenhaAtual ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <StyledTextField
            fullWidth
            label="Nova Senha"
            type={showNovaSenha ? 'text' : 'password'}
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            required
            disabled={loading || success}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNovaSenha(!showNovaSenha)}
                    edge="end"
                    disabled={loading || success}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showNovaSenha ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {novaSenha && (
            <StyledCard>
              <CardContent>
                <Typography 
                  variant="subtitle2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.9)'
                  }}
                >
                  Requisitos da senha:
                </Typography>
                <RequirementItem met={senhaRequirements.minLength} label="Mínimo de 8 caracteres" />
                <RequirementItem met={senhaRequirements.hasUpperCase} label="Pelo menos uma letra maiúscula" />
                <RequirementItem met={senhaRequirements.hasLowerCase} label="Pelo menos uma letra minúscula" />
                <RequirementItem met={senhaRequirements.hasNumber} label="Pelo menos um número" />
                <RequirementItem met={senhaRequirements.hasSpecialChar} label="Pelo menos um caractere especial" />
              </CardContent>
            </StyledCard>
          )}

          <StyledTextField
            fullWidth
            label="Confirmar Nova Senha"
            type={showConfirmarSenha ? 'text' : 'password'}
            value={confirmarSenha}
            onChange={(e) => setConfirmarSenha(e.target.value)}
            required
            disabled={loading || success}
            error={confirmarSenha && novaSenha !== confirmarSenha}
            helperText={confirmarSenha && novaSenha !== confirmarSenha ? 'As senhas não coincidem' : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                    edge="end"
                    disabled={loading || success}
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showConfirmarSenha ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <StyledButton
            type="submit"
            fullWidth
            disabled={loading || success || !validateSenha()}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: '#000000' }} />
            ) : success ? (
              'Senha Alterada!'
            ) : (
              'Alterar Senha'
            )}
          </StyledButton>
        </Box>
      </StyledPaper>
    </StyledContainer>
  );
};

export default TrocarSenha;



